/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving drugShipments.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugShipmentsState {
    drugShipments: Dtos.DrugShipment[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugShipmentsState {
    drugShipments: IInitialDrugShipmentsState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentsInitialState: IInitialDrugShipmentsState = {
    drugShipments: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentsSelector: TypedUseSelectorHook<IDrugShipmentsState> = useSelector;

export const DrugShipmentsSelectors = {
    selectDrugShipments: (state: IDrugShipmentsState) => state.drugShipments.drugShipments,
    selectLoadState: (state: IDrugShipmentsState) => state.drugShipments.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugShipmentsReducer extends ImmerReducer<IInitialDrugShipmentsState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(drugShipments?: Dtos.DrugShipment[]) {
        this.draftState.drugShipments = drugShipments ? drugShipments : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipments = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugShipmentsInitialState };
    }
};

export const DrugShipmentsActions = createActionCreators(DrugShipmentsReducer);
const reducer = createReducerFunction(DrugShipmentsReducer, DrugShipmentsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugShipmentsApi = {
    load: () => {
        return client.get(new Dtos.GetDrugShipments());
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentsLogic = {
    load: createLogic<IDrugShipmentsState, {}, undefined, string, ReturnType<typeof DrugShipmentsActions.load>>({
        type: DrugShipmentsActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await DrugShipmentsApi.load();

                dispatch(DrugShipmentsActions.loadSuccess(
                    response.drugShipments
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DrugShipmentsLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugShipments = (): [Dtos.DrugShipment[] | null, IRequestState<Dtos.ResponseStatus>, typeof DrugShipmentsActions] => {
    const drugShipments = useSelector(DrugShipmentsSelectors.selectDrugShipments);
    const loadState = useSelector(DrugShipmentsSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugShipmentsActions>(() => {
        return bindActionCreators(DrugShipmentsActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugShipmentsActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugShipments,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugShipments', reducer as any, logics as any);